/**
 * /birthday theme catalog — the opinionated core of the micro app.
 *
 * Each theme is a curated art direction: a palette for the picker card and a
 * prompt builder that writes a BETTER generation brief than a visitor would
 * type themselves. Three of the last 13 organic /new prompts were birthday
 * themes typed from scratch — this page is that demand, pre-art-directed.
 *
 * Prompts are written in the visitor's language (the spec model answers in
 * the prompt's language) and stay well under the server's 1000-char cap.
 */

import type { Lang } from '../new/regionCopy'
import cardScrapbook from './cards/card-scrapbook.webp'
import cardY2k from './cards/card-y2k.webp'
import cardFilm from './cards/card-film.webp'
import cardCoquette from './cards/card-coquette.webp'
import cardRetro from './cards/card-retro.webp'
import cardMidnight from './cards/card-midnight.webp'
import refScrapbook from './cards/ref-scrapbook.webp'
import refY2k from './cards/ref-y2k.webp'
import refFilm from './cards/ref-film.webp'
import refCoquette from './cards/ref-coquette.webp'
import refRetro from './cards/ref-retro.webp'
import refMidnight from './cards/ref-midnight.webp'

export interface BirthdayTheme {
  id: string
  /** Card title, already bilingual-safe (proper nouns mostly). */
  title: string
  tagline: { en: string; id: string }
  /** Picker card art direction. */
  card: {
    bg: string
    ink: string
    accent: string
    tape: string
    /** Big serif glyph drawn on the card (fallback when no thumb yet). */
    glyph: string
    /** Curated theme artwork shown on the picker card (text-free). */
    thumb?: string
  }
  /**
   * The SAME curated artwork at reference resolution, sent to /generate as a
   * style attachment — the frame-gen approach: we hand the designer model a
   * template that is already exactly our taste, and it designs the booth in
   * that world instead of improvising from adjectives alone.
   */
  styleRef?: string
  /** True for the one dark theme so the card text flips. */
  dark?: boolean
  prompt: (input: { name: string; age: string; extra: string }, lang: Lang) => string
}

const clean = (s: string): string => s.replace(/\s+/g, ' ').trim()

/** "Nabila" + "18" → "Nabila (18th)" / "Nabila (ke-18)" — or just the name. */
const who = (name: string, age: string, lang: Lang): string => {
  if (!age) return name
  return lang === 'id' ? `${name} (ulang tahun ke-${age})` : `${name} (${age}th birthday)`
}

const finish = (extra: string, lang: Lang): string =>
  extra ? (lang === 'id' ? ` Sentuhan tambahan: ${extra}.` : ` Extra touch: ${extra}.`) : ''

export const BIRTHDAY_THEMES: BirthdayTheme[] = [
  {
    id: 'scrapbook-diary',
    title: 'Scrapbook Diary',
    tagline: {
      en: 'Torn paper, doodles, cute stickers',
      id: 'Kertas sobek, doodle, stiker lucu'
    },
    card: { bg: '#F3E9D7', ink: '#4A3B2A', accent: '#C8322B', tape: '#F2C14E', glyph: 'S', thumb: cardScrapbook },
    styleRef: refScrapbook,
    prompt: ({ name, age, extra }, lang) =>
      clean(
        lang === 'id'
          ? `Photobooth ulang tahun untuk ${who(name, age, lang)} bertema scrapbook diary:
             kertas krem bertekstur, potongan kertas sobek, selotip washi, stiker dan doodle
             tangan yang lucu, foto polaroid miring, palet pastel hangat, tulisan tangan
             marker. Manis dan personal, bukan kekanak-kanakan. Headline WAJIB personal
             seperti tulisan sahabat — "Harinya ${name}!" atau "${name}, masih gemas" —
             JANGAN "HBD ${name}" polos, jangan salam generik.${finish(extra, lang)}`
          : `Birthday photobooth for ${who(name, age, lang)} in a scrapbook diary theme:
             textured cream paper, torn paper edges, washi tape, cute hand-drawn stickers
             and doodles, tilted polaroid photos, warm pastel palette, marker handwriting.
             Sweet and personal, not childish. The headline MUST read like a best friend
             wrote it — "It's ${name}'s day!" or "${name}, still adorable" — never a
             plain "HBD ${name}", never a generic greeting.${finish(extra, lang)}`
      )
  },
  {
    id: 'y2k-disco',
    title: 'Y2K Disco',
    tagline: {
      en: 'Chrome, candy pop, star stickers',
      id: 'Chrome, candy pop, stiker bintang'
    },
    card: { bg: '#E8E3F4', ink: '#2E2450', accent: '#C8322B', tape: '#88A9D2', glyph: 'Y', thumb: cardY2k },
    styleRef: refY2k,
    prompt: ({ name, age, extra }, lang) =>
      clean(
        lang === 'id'
          ? `Photobooth ulang tahun untuk ${who(name, age, lang)} bertema Y2K disco:
             teks chrome metalik, gradasi candy pop, stiker bintang dan hati, kilau bola
             disko, estetika 2000-an futuristik, warna ungu-pink-biru elektrik. Berani dan
             playful, tetap rapi. Headline bergaya cyber-personal — "${name.toUpperCase()}.EXE"
             atau "era barunya ${name}" — JANGAN "HBD ${name}" polos.${finish(extra, lang)}`
          : `Birthday photobooth for ${who(name, age, lang)} in a Y2K disco theme:
             metallic chrome text, candy-pop gradients, star and heart stickers, disco-ball
             sparkle, futuristic 2000s aesthetic, electric purple-pink-blue. Bold and
             playful but clean. Cyber-personal headline — "${name.toUpperCase()}.EXE" or
             "${name} enters a new era" — never a plain "HBD ${name}".${finish(extra, lang)}`
      )
  },
  {
    id: 'film-night',
    title: 'Film Night',
    tagline: {
      en: '35mm stills, grain, timestamp',
      id: 'Film 35mm, grain, timestamp'
    },
    card: { bg: '#201D18', ink: '#F1E9DA', accent: '#F2C14E', tape: '#88A9D2', glyph: 'F', thumb: cardFilm },
    styleRef: refFilm,
    dark: true,
    prompt: ({ name, age, extra }, lang) =>
      clean(
        lang === 'id'
          ? `Photobooth ulang tahun untuk ${who(name, age, lang)} bertema film analog:
             frame film 35mm, grain halus, timestamp oranye di sudut, kontras sinematik,
             nuansa malam yang hangat, tipografi serif kecil elegan. Dewasa dan estetik.
             Headline bergaya slate film — "${name.toUpperCase()} — TAKE ${age || "SATU"}"
             atau "${name}: THE PREMIERE" — JANGAN "HBD ${name}" polos.${finish(extra, lang)}`
          : `Birthday photobooth for ${who(name, age, lang)} in an analog film theme:
             35mm film frames, fine grain, orange timestamp in the corner, cinematic
             contrast, warm night tones, small elegant serif type. Grown-up and aesthetic.
             Film-slate headline — "${name.toUpperCase()} — TAKE ${age || "ONE"}" or
             "${name}: THE PREMIERE" — never a plain "HBD ${name}".${finish(extra, lang)}`
      )
  },
  {
    id: 'cherry-coquette',
    title: 'Cherry Coquette',
    tagline: {
      en: 'Bows, cherries, red on cream',
      id: 'Pita, ceri, merah di krem'
    },
    card: { bg: '#F9EFE4', ink: '#7A1E1E', accent: '#C8322B', tape: '#E7A6B7', glyph: 'C', thumb: cardCoquette },
    styleRef: refCoquette,
    prompt: ({ name, age, extra }, lang) =>
      clean(
        lang === 'id'
          ? `Photobooth ulang tahun untuk ${who(name, age, lang)} bertema cherry coquette:
             pita satin, ceri merah, renda halus, hati kecil, merah cherry di atas krem,
             tipografi serif feminin, estetika Pinterest coquette yang chic. Headline
             romantis-personal — "${name}, sweetest ${age || "girl"}" atau
             "hari ini milik ${name}" — JANGAN "HBD ${name}" polos.${finish(extra, lang)}`
          : `Birthday photobooth for ${who(name, age, lang)} in a cherry coquette theme:
             satin bows, red cherries, delicate lace, tiny hearts, cherry red on cream,
             feminine serif type, chic Pinterest coquette aesthetic. Romantic personal
             headline — "${name}, sweetest ${age || "girl"}" or "today belongs to
             ${name}" — never a plain "HBD ${name}".${finish(extra, lang)}`
      )
  },
  {
    id: 'retro-pop',
    title: 'Retro Pop 90s',
    tagline: {
      en: 'Bold shapes, funky colors',
      id: 'Bentuk berani, warna funky'
    },
    card: { bg: '#F5E8D0', ink: '#22306B', accent: '#E4572E', tape: '#7BB661', glyph: 'R', thumb: cardRetro },
    styleRef: refRetro,
    prompt: ({ name, age, extra }, lang) =>
      clean(
        lang === 'id'
          ? `Photobooth ulang tahun untuk ${who(name, age, lang)} bertema retro pop 90an:
             bentuk geometris berani, warna-warna funky oranye-biru-hijau, pola squiggle,
             tipografi tebal retro, stiker smiley, energi pesta 90an yang fun. Headline
             bubble tebal personal — "${name.toUpperCase()}${age ? ` ${age}!` : " PESTA!"}"
             atau "PESTANYA ${name.toUpperCase()}" — JANGAN "HBD ${name}" polos.${finish(extra, lang)}`
          : `Birthday photobooth for ${who(name, age, lang)} in a retro 90s pop theme:
             bold geometric shapes, funky orange-blue-green colors, squiggle patterns,
             chunky retro type, smiley stickers, fun 90s party energy. Chunky personal
             bubble headline — "${name.toUpperCase()}${age ? ` ${age}!` : " PARTY!"}" or
             "${name.toUpperCase()} THROWS A PARTY" — never a plain "HBD ${name}".${finish(extra, lang)}`
      )
  },
  {
    id: 'midnight-glow',
    title: 'Midnight Glow',
    tagline: {
      en: 'Sparkle & glow, for night parties',
      id: 'Sparkle & glow, buat pesta malam'
    },
    card: { bg: '#171430', ink: '#EFE9FF', accent: '#F2C14E', tape: '#8E7CFF', glyph: 'M', thumb: cardMidnight },
    styleRef: refMidnight,
    dark: true,
    prompt: ({ name, age, extra }, lang) =>
      clean(
        lang === 'id'
          ? `Photobooth ulang tahun untuk ${who(name, age, lang)} bertema midnight glow:
             langit malam biru gelap, kilau bintang dan glitter, cahaya lilin keemasan,
             sparkle lembut, tipografi serif elegan bercahaya, mewah dan magis untuk pesta
             malam atau sweet seventeen. Headline berpendar personal — "${name}, ${age ? `${age} ` : ""}& bersinar"
             atau "malam ini milik ${name}" — JANGAN "HBD ${name}" polos.${finish(extra, lang)}`
          : `Birthday photobooth for ${who(name, age, lang)} in a midnight glow theme:
             deep navy night sky, star glitter, golden candlelight, soft sparkle, elegant
             glowing serif type, luxurious and magical for a night party or sweet
             seventeen. Glowing personal headline — "${name}, ${age ? `${age} ` : ""}& shining"
             or "the night belongs to ${name}" — never a plain "HBD ${name}".${finish(extra, lang)}`
      )
  }
]
